import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Loader2, Check } from "lucide-react";
import { subscribeNewsletter } from "@/lib/api";

const EASE = [0.76, 0, 0.24, 1];

export const NewsletterSignup = () => {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [message, setMessage] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus("loading");
    setMessage("");
    try {
      const r = await subscribeNewsletter({ email: email.trim() });
      setStatus("success");
      setMessage(
        r.status === "already_subscribed"
          ? "You're already on the list — watch your inbox."
          : "You're on the list. We'll email you when the next essay goes live."
      );
      setEmail("");
    } catch (err) {
      setStatus("error");
      const d = err?.response?.data?.detail;
      setMessage(
        typeof d === "string"
          ? d
          : "That didn't work — please check the email and try again."
      );
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.8, ease: EASE }}
      className="border-b border-[var(--line-strong)] bg-[var(--bg-2)]"
      data-testid="newsletter-signup"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-12 md:py-16 grid md:grid-cols-12 gap-8 md:items-center">
        <div className="md:col-span-6">
          <p className="overline mb-4">Newsletter</p>
          <h2 className="font-display text-3xl md:text-4xl tracking-[-0.02em] leading-[1.02] text-[var(--ink)]">
            New essays,{" "}
            <span className="font-serif-accent italic text-[var(--accent)]">
              straight to your inbox
            </span>
            .
          </h2>
          <p className="mt-4 text-sm text-[var(--ink-70)] leading-relaxed max-w-md">
            One email when a new essay goes live. No noise, unsubscribe anytime.
          </p>
        </div>
        <div className="md:col-span-6">
          {status === "success" ? (
            <div
              className="flex items-start gap-3 border border-[var(--accent)]/50 bg-[var(--bg)] px-5 py-4"
              data-testid="newsletter-success-message"
            >
              <Check className="w-4 h-4 mt-0.5 text-[var(--accent)] shrink-0" strokeWidth={2} />
              <p className="text-sm text-[var(--ink)] leading-relaxed">{message}</p>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="flex flex-col sm:flex-row sm:items-end gap-4">
              <div className="flex-1">
                <label htmlFor="newsletter-email" className="overline block mb-2">
                  Email
                </label>
                <input
                  id="newsletter-email"
                  type="email"
                  required
                  className="editorial-input"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  data-testid="newsletter-email-input"
                />
              </div>
              <button
                type="submit"
                disabled={status === "loading"}
                className="btn-pill disabled:opacity-60 shrink-0"
                data-testid="newsletter-submit-btn"
              >
                {status === "loading" ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Subscribing…</span>
                  </>
                ) : (
                  <>
                    <span>Subscribe</span>
                    <ArrowUpRight className="w-4 h-4" strokeWidth={1.6} />
                  </>
                )}
              </button>
            </form>
          )}
          {status === "error" && (
            <p className="mt-3 text-sm text-red-600" data-testid="newsletter-error-message">
              {message}
            </p>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default NewsletterSignup;
